/* eslint-disable react-hooks/exhaustive-deps */
import {
  UseFormRegister,
  RegisterOptions,
  UseFormClearErrors,
  UseFormSetValue,
} from "react-hook-form";
import { useState, useEffect, useRef } from "react";
import { IconChevronDown, IconSearch, IconX } from "@tabler/icons-react";

type SelectOption = {
  id?: string | number;
  value: string | number;
  text: string;
};

type Props = {
  name: string;
  label?: string;
  placeholder?: string;
  selectOptions: SelectOption[];
  register?: UseFormRegister<any>;
  validation?: RegisterOptions;
  error?: any;
  className?: string;
  labelClass?: string;
  disabled?: boolean;
  removeDiv?: boolean;
  value?: string | any;
  setValue?: (val: string | any) => void;
  onChange?: (val: string | any) => void;
  clearErrors?: UseFormClearErrors<any>;
  formSetValue?: UseFormSetValue<any>;
  other?: any;
};

export default function NewSelectInput({
  name,
  label,
  placeholder = "Select",
  selectOptions,
  register,
  validation,
  error,
  className = "",
  labelClass = "",
  disabled = false,
  removeDiv = false,
  value,
  setValue,
  onChange,
  clearErrors,
  formSetValue,
  other,
}: Props) {
  const [selectOpen, setSelectOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = selectOptions?.filter((opt) =>
    opt?.text?.toLowerCase()?.includes(searchText?.toLowerCase())
  );

  const selectedText =
    selectOptions?.find((opt) => String(opt.value) === String(value))?.text || "";

  const hasValue = value != null && value !== "";

  // Clear selection – accepts both Mouse & Keyboard events
  const handleClear = (
    e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLDivElement>
  ) => {
    e.stopPropagation();
    if (setValue) setValue("");
    if (onChange) onChange("");
    if (clearErrors) clearErrors(name);
    if (formSetValue) formSetValue(name, "");
    setSearchText("");
    setSelectOpen(false);
  };

  const handleSelect = (val: string | number) => {
    if (setValue) setValue(val);
    if (onChange) onChange(val);
    if (clearErrors) clearErrors(name);
    if (formSetValue) formSetValue(name, val);
    setSelectOpen(false);
    setSearchText("");
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Toggle dropdown + direction
  const handleDropdownToggle = () => {
    if (disabled) return;

    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUpward(spaceBelow < 250 && spaceAbove > 250);
    }

    setSelectOpen((prev) => !prev);
    if (!selectOpen) setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Keyboard support – fixed typing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectOpen) return;

      if (e.key === "Escape") {
        setSelectOpen(false);
        setSearchText("");
      }

      // Backspace clears when search is empty
      if (e.key === "Backspace" && searchText === "" && hasValue) {
        // Create a fake React.KeyboardEvent to pass to handleClear
        const fakeEvent = {
          stopPropagation: () => {},
          currentTarget: dropdownRef.current!,
        } as React.KeyboardEvent<HTMLDivElement>;

        handleClear(fakeEvent);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectOpen, searchText, hasValue]);

  const labelNode = label && (
    <label
      htmlFor={name}
      className={`text-[#1E293B] text-sm ${labelClass} whitespace-nowrap`}
    >
      {label}
      {validation?.required && <span className="text-red-600 ml-1">*</span>}
    </label>
  );

  const dropdown = (
    <div
      className={`absolute z-30 left-0 w-full rounded-xl bg-white border border-gray-200 shadow-lg max-h-60 overflow-y-auto ${
        openUpward ? "bottom-full mb-1" : "top-full mt-1"
      }`}
    >
      <div className="p-2">
        <div className="flex items-center gap-2 px-2 py-1 bg-gray-100 rounded-full">
          <IconSearch size={16} className="text-gray-500" />
          <input
            ref={inputRef}
            type="text"
            className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
      <ul>
        {filteredOptions?.map((opt, i) => (
          <li
            key={opt.id ?? i}
            className={`px-4 py-2 text-sm hover:bg-blue-100 cursor-pointer ${
              String(opt.value) === String(value) ? "bg-blue-50 font-medium" : ""
            }`}
            onClick={() => handleSelect(opt.value)}
          >
            {opt.text}
          </li>
        ))}
        {filteredOptions?.length === 0 && (
          <li className="px-4 py-2 text-sm text-gray-400">No results found</li>
        )}
      </ul>
    </div>
  );

  const selectField = (
    <div ref={dropdownRef} className="relative w-full">
      <div
        className={`flex items-center justify-between px-4 py-2 text-sm rounded-full border ${
          error ? "border-red-500" : "border-gray-200"
        } bg-white cursor-pointer ${disabled ? "opacity-50" : ""}`}
        onClick={handleDropdownToggle}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleDropdownToggle();
          }
        }}
      >
        <span className="truncate text-[#1E1E1E] flex-1">
          {selectedText || placeholder}
        </span>

        <div className="flex items-center gap-1">
          {/* Clear Button */}
          {hasValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 rounded-full hover:bg-gray-200 transition-colors"
              aria-label="Clear selection"
            >
              <IconX size={16} className="text-gray-500" />
            </button>
          )}
          <IconChevronDown
            size={18}
            stroke={2.2}
            className={`text-gray-500 transition-transform ${
              selectOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {selectOpen && dropdown}

      {/* Hidden input for RHF */}
      {register && !formSetValue && (
        <input
          type="hidden"
          {...register(name, validation)}
          value={value ?? ""}
        />
      )}
    </div>
  );

  return (
    <div className={`space-y-1 relative ${className}`}>
      {labelNode}
      <div className="w-full">
        {removeDiv ? (
          selectField
        ) : (
          <div className="relative">{selectField}</div>
        )}
        <span className="text-red-500 text-sm block pl-3">
          {error?.message || ""}
        </span>
      </div>
    </div>
  );
}
